import * as React from 'react';
import { CloseLine, CheckCircleLine, WarningLine, InformationLine, AlertLine } from '@mingcute/react';

import { cn } from '../lib/cn';
import {
  type ToastVariant,
  type ToastEvent,
  type ToastOpts,
  TOAST_EVENT_NAME,
} from '../lib/toast';

/**
 * Klaret Toast (handoff §10.9).
 * The Klaret signature: inverted ink bar, top-center, slides down 12px (d-slow).
 * --tvl-ink fill, paper text, --tvl-r3, --tvl-e2, max-width 480px, 12/16px padding.
 * Leading 20px status icon; optional single tertiary action; trailing close.
 * One visible at a time; queue behind; 5s auto-dismiss (pause on hover; persists if action).
 * Radix removed entirely. Public API (toast(), Toaster, and all re-exports) unchanged.
 */

/* ── Re-export lib types so consumers can import from the component ──────── */
export type { ToastVariant, ToastEvent, ToastOpts };

/* ── Icon map ────────────────────────────────────────────────────────────── */
const ICONS: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircleLine size={20} color="currentColor" aria-hidden />,
  warning: <AlertLine size={20} color="currentColor" aria-hidden />,
  danger: <WarningLine size={20} color="currentColor" aria-hidden />,
  info: <InformationLine size={20} color="currentColor" aria-hidden />,
};

/* ── Single toast item component ─────────────────────────────────────────── */
interface ToastItemProps {
  event: ToastEvent;
  onDismiss: (id: string) => void;
}

function ToastItem({ event, onDismiss }: ToastItemProps) {
  const { id, variant, message, title, action, duration = 5000 } = event;
  const hasAction = Boolean(action);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const startTimer = React.useCallback(() => {
    if (hasAction) return; // persists if it carries an action
    timerRef.current = setTimeout(() => onDismiss(id), duration);
  }, [id, duration, hasAction, onDismiss]);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    startTimer();
    return clearTimer;
  }, [startTimer, clearTimer]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      onMouseEnter={clearTimer}
      onMouseLeave={startTimer}
      className={cn(
        /* inverted ink surface */
        'pointer-events-auto flex w-full max-w-[480px] items-start gap-3',
        'bg-[var(--tvl-ink)] text-[var(--tvl-raised)]',
        'rounded-[var(--tvl-r3)] shadow-[var(--tvl-e2)]',
        'px-4 py-3',
        /* entrance: slide down 12px */
        'animate-[toast-enter_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
      )}
    >
      {/* status icon */}
      <span className="mt-0.5 shrink-0">{ICONS[variant]}</span>

      {/* body */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {title ? (
          <p className="text-sm font-semibold leading-tight">{title}</p>
        ) : null}
        <p className="text-sm leading-snug">{message}</p>
        {action ? (
          <button
            onClick={() => {
              action.onClick();
              onDismiss(id);
            }}
            className={cn(
              'mt-1 self-start text-xs underline-offset-2',
              'text-[var(--tvl-raised)] opacity-80 hover:opacity-100 hover:underline',
              'transition-opacity duration-[var(--tvl-d-fast)]',
            )}
          >
            {action.label}
          </button>
        ) : null}
      </div>

      {/* close */}
      <button
        onClick={() => onDismiss(id)}
        aria-label="Dismiss"
        className={cn(
          'mt-0.5 shrink-0 rounded-[var(--tvl-r1)] p-0.5',
          'text-[var(--tvl-raised)] opacity-60 hover:opacity-100',
          'transition-opacity duration-[var(--tvl-d-fast)]',
        )}
      >
        <CloseLine size={16} color="currentColor" aria-hidden />
      </button>
    </div>
  );
}

/* ── Toaster (the viewport) ──────────────────────────────────────────────── */
export function Toaster() {
  const [queue, setQueue] = React.useState<ToastEvent[]>([]);

  React.useEffect(() => {
    function handle(e: Event) {
      const detail = (e as CustomEvent<ToastEvent>).detail;
      // one at a time: replace queue or append (queue behind)
      setQueue((prev) => [...prev, detail]);
    }
    window.addEventListener(TOAST_EVENT_NAME, handle);
    return () => window.removeEventListener(TOAST_EVENT_NAME, handle);
  }, []);

  const dismiss = React.useCallback((id: string) => {
    setQueue((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Only the first item is visible; the rest wait in queue
  const visible = queue[0];

  return (
    <div
      aria-label="Notifications"
      className={cn(
        'fixed inset-x-0 top-0 z-[var(--tvl-z-toast)] flex justify-center',
        // Below the status bar, or every confirmation and error appears behind the clock.
        'pointer-events-none px-4 pt-[calc(0.75rem+var(--tvl-safe-top))]',
      )}
    >
      {visible ? (
        <ToastItem key={visible.id} event={visible} onDismiss={dismiss} />
      ) : null}
    </div>
  );
}

/* ── Legacy component exports (kept for consumers that import them) ───────── */

/** @deprecated Use the `toast()` helper and `<Toaster />` instead. */
export const ToastProvider: React.FC<{
  children?: React.ReactNode;
  /** Radix compat — ignored in the RAC implementation. */
  swipeDirection?: 'up' | 'down' | 'left' | 'right';
  /** Radix compat — ignored in the RAC implementation. */
  duration?: number;
}> = ({ children }) => <>{children}</>;

/** @deprecated Viewport is built into <Toaster />. Kept for API compat. */
export const ToastViewport = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'fixed inset-x-0 top-0 z-[var(--tvl-z-toast)] flex justify-center pointer-events-none px-4',
      'pt-[calc(0.75rem+var(--tvl-safe-top))]',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = 'ToastViewport';

export interface ToastProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: ToastVariant;
  /** Radix compat — auto-dismiss duration in ms. Handled by Toaster internally. */
  duration?: number;
  /** Radix compat — called when the toast open state changes. */
  onOpenChange?: (open: boolean) => void;
}

/** @deprecated Compose via toast() + Toaster. Kept for API compat. */
export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ className, variant = 'info', duration: _duration, onOpenChange: _onOpenChange, ...props }, ref) => (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      className={cn(
        'pointer-events-auto flex w-full max-w-[480px] items-start gap-3',
        'bg-[var(--tvl-ink)] text-[var(--tvl-raised)]',
        'rounded-[var(--tvl-r3)] shadow-[var(--tvl-e2)] px-4 py-3',
        className,
      )}
      {...props}
    />
  ),
);
Toast.displayName = 'Toast';

export function ToastIcon({ variant }: { variant: ToastVariant }) {
  return <span className="mt-0.5 shrink-0">{ICONS[variant]}</span>;
}

export const ToastTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm font-semibold leading-tight', className)} {...props} />
));
ToastTitle.displayName = 'ToastTitle';

export const ToastDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm leading-snug opacity-80', className)} {...props} />
));
ToastDescription.displayName = 'ToastDescription';

export interface ToastActionProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Radix compat — used as accessible label in the old Radix API. */
  altText?: string;
}

export const ToastAction = React.forwardRef<HTMLButtonElement, ToastActionProps>(
  ({ className, altText: _altText, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'mt-1 self-start text-xs underline-offset-2',
        'text-[var(--tvl-raised)] opacity-80 hover:opacity-100 hover:underline',
        'transition-opacity duration-[var(--tvl-d-fast)]',
        className,
      )}
      {...props}
    />
  ),
);
ToastAction.displayName = 'ToastAction';

export const ToastClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    aria-label="Dismiss"
    className={cn(
      'mt-0.5 shrink-0 rounded-[var(--tvl-r1)] p-0.5',
      'text-[var(--tvl-raised)] opacity-60 hover:opacity-100',
      'transition-opacity duration-[var(--tvl-d-fast)]',
      className,
    )}
    {...props}
  >
    <CloseLine size={16} color="currentColor" aria-hidden />
  </button>
));
ToastClose.displayName = 'ToastClose';
